import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadFile, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Check, Upload, ShieldCheck, Loader2 } from "lucide-react";

const REQUIRED = [
    {
        key: "trade_license_upload_id",
        label: "Shop / Trade License",
        hint: "GST, Municipality or Panchayat license.",
        required: true,
    },
    {
        key: "pan_or_aadhaar_upload_id",
        label: "PAN or Aadhaar",
        hint: "Owner's PAN or Aadhaar (single doc).",
        required: true,
    },
    {
        key: "shop_photo_upload_id",
        label: "Shop Photo / Offer Proof",
        hint: "Optional but boosts approval speed.",
        required: false,
    },
    {
        key: "address_proof_upload_id",
        label: "Address Proof",
        hint: "Optional — utility bill or agreement.",
        required: false,
    },
];

export default function MerchantOnboarding() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [docs, setDocs] = useState({});
    const [busyKey, setBusyKey] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    if (!user) return <Navigate to="/merchant/login" replace />;
    if (user.role !== "SHOP_OWNER") return <Navigate to="/dashboard" replace />;

    const canSubmit =
        !!docs.trade_license_upload_id && !!docs.pan_or_aadhaar_upload_id;

    const upload = async (key, file) => {
        try {
            setBusyKey(key);
            const up = await uploadFile(file, "verification_doc");
            setDocs((prev) => ({ ...prev, [key]: up.id, [`${key}__meta`]: up }));
            toast.success("Uploaded");
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusyKey(null);
        }
    };

    const submit = async () => {
        try {
            setSubmitting(true);
            await api.patch("/auth/complete-shop-docs", {
                trade_license_upload_id: docs.trade_license_upload_id,
                pan_or_aadhaar_upload_id: docs.pan_or_aadhaar_upload_id,
                shop_photo_upload_id: docs.shop_photo_upload_id,
                address_proof_upload_id: docs.address_proof_upload_id,
            });
            await refresh();
            toast.success("Submitted for review");
            navigate("/pending", { replace: true });
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50" data-testid="merchant-onboarding-page">
            <div className="mx-auto flex min-h-screen max-w-[560px] flex-col bg-white">
                <header className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-4">
                    <Logo size={48} className="sm:hidden" />
                    <Logo size={56} className="hidden sm:flex" />
                    <div className="text-xs font-semibold text-orange-600">Verification</div>
                </header>
                <main className="flex-1 px-5 pb-24 pt-6">
                    <div className="mb-5 flex items-start gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-4">
                        <ShieldCheck className="mt-0.5 shrink-0 text-orange-600" size={18} />
                        <div>
                            <div className="text-sm font-bold text-neutral-800">
                                Verify your business
                            </div>
                            <div className="mt-1 text-xs text-neutral-600">
                                Upload 2 required docs. Our team reviews in
                                under 24 hours.
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {REQUIRED.map((f) => {
                            const uploaded = docs[f.key];
                            const meta = docs[`${f.key}__meta`];
                            const busy = busyKey === f.key;
                            return (
                                <label
                                    key={f.key}
                                    className={`flex cursor-pointer flex-col rounded-2xl border-2 border-dashed p-4 ${uploaded ? "border-emerald-500 bg-emerald-50" : "border-neutral-300 bg-neutral-50"}`}
                                    data-testid={`mo-doc-${f.key}`}
                                >
                                    <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
                                        {busy ? (
                                            <Loader2 className="animate-spin text-orange-500" size={16} />
                                        ) : uploaded ? (
                                            <Check className="text-emerald-600" size={16} />
                                        ) : (
                                            <Upload size={16} />
                                        )}
                                        {f.label}
                                        {f.required && (
                                            <span className="ml-1 text-rose-600">*</span>
                                        )}
                                    </div>
                                    <div className="mt-1 text-xs text-neutral-500">{f.hint}</div>
                                    {uploaded ? (
                                        <div className="mt-3 truncate text-xs text-emerald-700">
                                            Uploaded ✔ {meta?.filename || ""}
                                        </div>
                                    ) : (
                                        <div className="mt-3 text-xs text-neutral-400">
                                            Tap to choose an image or PDF…
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*,application/pdf"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) upload(f.key, file);
                                        }}
                                    />
                                </label>
                            );
                        })}
                    </div>
                </main>
                <div className="sticky bottom-0 border-t border-neutral-200 bg-white p-4">
                    <Button
                        className="h-12 w-full nest-gradient text-base font-semibold text-white"
                        disabled={!canSubmit || submitting}
                        onClick={submit}
                        data-testid="mo-submit"
                    >
                        {submitting ? "Submitting…" : "Submit for review"}
                    </Button>
                    <p className="mt-2 text-center text-xs text-neutral-500">
                        You can add optional docs later from your profile.
                    </p>
                </div>
            </div>
        </div>
    );
}
